// Skill matching and allocation utilities

/**
 * Allocate all projects to people based on skills and availability
 */
export const allocateAllProjects = (data) => {
  const rows = [];
  const perProject = new Map();
  
  // Create a deep copy of people data to avoid mutating the original
  const peopleCopy = data.people.map(person => ({
    ...person,
    hoursAvail: person.hoursAvail,
    skills: [...person.skills]
  }));
  
  data.projects.forEach(project => {
    let unassignedHours = 0;
    
    project.requires.forEach(requirement => {
      const { skill, level, hours } = requirement;
      
      // Find best available person for this skill requirement
      const bestMatch = peopleCopy
        .filter(person => {
          const personSkill = person.skills.find(s => s.skill === skill);
          return personSkill && personSkill.level >= level && person.hoursAvail > 0;
        })
        .sort((a, b) => {
          const aSkill = a.skills.find(s => s.skill === skill);
          const bSkill = b.skills.find(s => s.skill === skill);
          // Sort by skill level (desc) then by availability (desc)
          if (bSkill.level !== aSkill.level) return bSkill.level - aSkill.level;
          return b.availability - a.availability;
        })[0];
      
      if (bestMatch) {
        const personSkill = bestMatch.skills.find(s => s.skill === skill);
        const allocatedHours = Math.min(hours, bestMatch.hoursAvail);
        
        rows.push({
          projectId: project.id,
          projectTitle: project.title,
          skill,
          level,
          hours: allocatedHours,
          personId: bestMatch.id,
          personName: bestMatch.name,
          personLevel: personSkill.level
        });
        
        bestMatch.hoursAvail -= allocatedHours;
        
        if (allocatedHours < hours) {
          unassignedHours += (hours - allocatedHours);
        }
      } else {
        unassignedHours += hours;
        rows.push({
          projectId: project.id,
          projectTitle: project.title,
          skill,
          level,
          hours,
          personId: null,
          personName: "UNASSIGNED",
          personLevel: null
        });
      }
    });
    
    perProject.set(project.id, unassignedHours);
  });
  
  return { rows, perProject };
};

/**
 * Score a person for a specific project based on skills match
 */
export const scorePersonForProject = (person, project) => {
  let totalScore = 0;
  let totalWeight = 0;
  
  project.requires.forEach(requirement => {
    const { skill, level, hours } = requirement;
    const personSkill = person.skills.find(s => s.skill === skill);
    
    if (personSkill) {
      // Skill level match (0-1)
      const levelScore = Math.min(personSkill.level / level, 1);
      
      // Years of experience bonus (0-0.2)
      const experienceBonus = Math.min(personSkill.years / 10, 0.2);
      
      // Validation bonus (0-0.1)
      const validationBonus = personSkill.validated ? 0.1 : 0;
      
      // Availability factor (0-1)
      const availabilityScore = person.availability;
      
      const skillScore = (levelScore + experienceBonus + validationBonus) * availabilityScore;
      const weight = hours; // Weight by hours required
      
      totalScore += skillScore * weight;
      totalWeight += weight;
    }
  });
  
  return totalWeight > 0 ? totalScore / totalWeight : 0;
};

/**
 * Analyze skill gaps across all projects
 */
export const analyzeSkillGaps = (data) => {
  const gaps = [];
  
  data.projects.forEach(project => {
    project.requires.forEach(requirement => {
      const { skill, level, hours, importanceLabel } = requirement;
      
      // Find people with this skill at required level
      const qualifiedPeople = data.people.filter(person => {
        const personSkill = person.skills.find(s => s.skill === skill);
        return personSkill && personSkill.level >= level;
      });
      
      // Calculate total available hours from qualified people
      const totalAvailableHours = qualifiedPeople.reduce((sum, person) => {
        return sum + Math.min(person.hoursAvail, hours);
      }, 0);
      
      const gap = Math.max(0, hours - totalAvailableHours);
      
      if (gap > 0) {
        gaps.push({
          skill,
          project: project.title,
          requiredLevel: level,
          requiredHours: hours,
          availableHours: totalAvailableHours,
          gap,
          importance: importanceLabel || (project.priority === 'High' ? 'Critical' : 
                     project.priority === 'Medium' ? 'High' : 'Medium')
        });
      }
    });
  });
  
  // Sort by importance and gap size
  return gaps.sort((a, b) => {
    const importanceOrder = { 'Critical': 3, 'High': 2, 'Medium': 1 };
    if (importanceOrder[b.importance] !== importanceOrder[a.importance]) {
      return importanceOrder[b.importance] - importanceOrder[a.importance];
    }
    return b.gap - a.gap;
  });
};

/**
 * Analyze team capacity by team
 */
export const analyzeTeamCapacity = (data) => {
  const teams = {};
  
  data.people.forEach(person => {
    if (!teams[person.team]) {
      teams[person.team] = {
        totalPeople: 0,
        totalHours: 0,
        avgAvailability: 0
      };
    }
    
    teams[person.team].totalPeople++;
    teams[person.team].totalHours += person.hoursAvail;
    teams[person.team].avgAvailability += person.availability;
  });
  
  // Calculate averages
  Object.values(teams).forEach(team => {
    team.avgAvailability = team.avgAvailability / team.totalPeople;
  });
  
  return teams;
};

/**
 * Get certification status based on expiration date
 */
export const getCertificationStatus = (cert) => {
  const now = new Date();
  const expiry = new Date(cert.expires);
  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) return "Expired";
  if (daysUntilExpiry <= 30) return "Expiring Soon";
  if (daysUntilExpiry <= 90) return "Warning";
  return "Valid";
};

/**
 * Get color for certification status
 */
export const getCertificationColor = (cert) => {
  const status = getCertificationStatus(cert);
  switch (status) {
    case "Expired": return "red";
    case "Expiring Soon": return "red";
    case "Warning": return "orange";
    case "Valid": return "green";
    default: return "gray";
  }
};

/**
 * Calculate days until certification expires
 */
export const expiresInDays = (expiryDate) => {
  const now = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
};

/**
 * Check if certification is expired
 */
export const isExpired = (expiryDate) => {
  return expiresInDays(expiryDate) < 0;
};

/**
 * Assess project risk based on various factors
 */
export const assessProjectRisk = (project, people) => {
  const risks = [];
  
  // Skill gap risk
  if (project.requires && project.requires.length > 0) {
    const skillGaps = project.requires.filter(req => {
      const qualifiedPeople = people.filter(person => {
        const personSkill = person.skills?.find(s => s.skill === req.skill);
        return personSkill && personSkill.level >= req.level;
      });
      return qualifiedPeople.length === 0;
    });
    
    if (skillGaps.length > 0) {
      risks.push({
        type: 'Skill Gap',
        description: `${skillGaps.length} required skills have no qualified people`,
        impact: 'High - Project may be delayed or fail',
        severity: 'High'
      });
    }
  }
  
  // Timeline risk
  if (project.deadline) {
    const daysUntilDeadline = Math.ceil((new Date(project.deadline) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntilDeadline < 30) {
      risks.push({
        type: 'Timeline',
        description: 'Project deadline is less than 30 days away',
        impact: 'Medium - May require additional resources',
        severity: 'Medium'
      });
    }
  }
  
  // Budget risk
  if (project.budget && project.budget > 100000) {
    risks.push({
      type: 'Budget',
      description: 'High-budget project requires careful monitoring',
      impact: 'Medium - Financial oversight needed',
      severity: 'Medium'
    });
  }
  
  return risks;
};

/**
 * Calculate person performance metrics
 */
export const calculatePersonPerformance = (person, projects) => {
  // This is a simplified calculation - in a real app you'd have more data
  const assignedProjects = projects.filter(p => 
    p.currentTeam && p.currentTeam.includes(person.id)
  ).length;
  
  const totalHours = person.hoursAvail || 0;
  const completionRate = assignedProjects > 0 ? 85 : 0; // Mock completion rate
  const skillUtilization = person.skills?.length || 0;
  const performance = Math.min(100, (completionRate + skillUtilization * 10) / 2);
  
  return {
    assignedProjects,
    totalHours,
    completionRate,
    skillUtilization,
    performance: Math.round(performance)
  };
};

/**
 * Get recommendations for a person
 */
export const getRecommendations = (person, projects, allPeople) => {
  const recommendations = [];
  
  // Skill development recommendation
  if (person.skills && person.skills.length < 5) {
    recommendations.push({
      type: 'Skill Development',
      description: 'Consider adding more skills to increase project opportunities',
      impact: 'Medium - Better project matching',
      priority: 'Medium'
    });
  }
  
  // Certification recommendation
  if (!person.certs || person.certs.length === 0) {
    recommendations.push({
      type: 'Certification',
      description: 'Obtain relevant certifications to improve credibility',
      impact: 'High - Better career opportunities',
      priority: 'High'
    });
  }
  
  // Availability recommendation
  if (person.availability < 0.5) {
    recommendations.push({
      type: 'Availability',
      description: 'Increase availability to take on more projects',
      impact: 'Medium - More project opportunities',
      priority: 'Medium'
    });
  }
  
  return recommendations;
};
